// src/components/roads/roadSegmentHealth.js
// --- UPDATE (GIS map — road segment health layer) -----------------------------
// Pure helpers (no React / Leaflet) for drawing road segments by health.
//
//   SEGMENT_BANDS / bandOf(pci) / colorForPci(pci)   PCI → health band + colour
//   treatmentFor(pci)                                 FlowSense rule treatment (same thresholds as
//                                                     roadSegmentationService.actionForPci)
//   segmentPositions(segment, road)                   [[lat,lng],…] for a segment
//   bandCounts(segments) / summarize(segments)        legend + toolbar numbers
//   filterSegments(segments, visibleBands)            band toggle filter
//
// Bands follow the Road Works report: < 50 is "poor" and < 35 "critical" (report summary
// queries), ≥ 85 / 70 / 50 are the treatment breakpoints. Unscored segments stay grey —
// a missing PCI is never shown as healthy.
// --- END UPDATE ---------------------------------------------------------------
// --- UPDATE (roads v1.4): describeSegment adds the rain-water forecast and TessaDEM grade; summarize counts
// segments with standing water (`ponding`) and with ground elevation (`withGround`).
// --- END UPDATE ---------------------------------------------------------------

export const SEGMENT_BANDS = Object.freeze([
    { key: 'good', label: 'Good', range: '85–100', min: 85, color: '#38A169', scheme: 'green' },
    { key: 'satisfactory', label: 'Satisfactory', range: '70–84', min: 70, color: '#9AD94F', scheme: 'teal' },
    { key: 'fair', label: 'Fair', range: '50–69', min: 50, color: '#ECC94B', scheme: 'yellow' },
    { key: 'poor', label: 'Poor', range: '35–49', min: 35, color: '#ED8936', scheme: 'orange' },
    { key: 'critical', label: 'Critical', range: '0–34', min: -Infinity, color: '#E53E3E', scheme: 'red' },
    { key: 'unscored', label: 'Not scored', range: '—', min: null, color: '#A0AEC0', scheme: 'gray' },
]);
export const BAND_KEYS = SEGMENT_BANDS.map((b) => b.key);
const BY_KEY = Object.fromEntries(SEGMENT_BANDS.map((b) => [b.key, b]));

const num = (v) => {
    if (v === null || v === undefined || v === '') return null;
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
};

export const bandOf = (pci) => {
    const p = num(pci);
    if (p === null) return BY_KEY.unscored;
    return SEGMENT_BANDS.find((b) => b.min !== null && p >= b.min) || BY_KEY.critical;
};
export const bandByKey = (key) => BY_KEY[key] || BY_KEY.unscored;
export const colorForPci = (pci) => bandOf(pci).color;

/** Same breakpoints as the backend rules engine (actionForPci / remainingLifeForPci). */
export const treatmentFor = (pci) => {
    const p = num(pci);
    if (p === null) return null;
    if (p >= 85) return { code: 'routine_maintenance', label: 'Routine maintenance / fog seal' };
    if (p >= 70) return { code: 'preventive_crack_seal', label: 'Crack sealing & micro-surfacing' };
    if (p >= 50) return { code: 'mill_and_overlay', label: 'Mill & 50 mm overlay' };
    return { code: 'reconstruction', label: 'Structural reconstruction' };
};

// ── geometry ────────────────────────────────────────────────────────────────
const R = 6371000;
const rad = (d) => (d * Math.PI) / 180;
export const haversineM = (a, b) => {
    const dLat = rad(b.lat - a.lat);
    const dLng = rad(b.lng - a.lng);
    const x = Math.sin(dLat / 2) ** 2 + Math.cos(rad(a.lat)) * Math.cos(rad(b.lat)) * Math.sin(dLng / 2) ** 2;
    return 2 * R * Math.asin(Math.min(1, Math.sqrt(x)));
};
const lerp = (a, b, t) => ({ lat: a.lat + (b.lat - a.lat) * t, lng: a.lng + (b.lng - a.lng) * t });

/** Part of a polyline between two distances (metres) from its start. */
export function slicePolyline(points, fromM, toM) {
    if (!Array.isArray(points) || points.length < 2) return [];
    const out = [];
    let acc = 0;
    for (let i = 0; i < points.length - 1; i++) {
        const a = points[i]; const b = points[i + 1];
        const leg = haversineM(a, b);
        if (leg <= 0) continue;
        const legStart = acc; const legEnd = acc + leg;
        if (legEnd >= fromM && legStart <= toM) {
            const t0 = Math.max(0, (fromM - legStart) / leg);
            const t1 = Math.min(1, (toM - legStart) / leg);
            const p0 = lerp(a, b, t0);
            if (!out.length) out.push(p0);
            out.push(lerp(a, b, t1));
        }
        acc = legEnd;
        if (acc > toM) break;
    }
    return out;
}
export const polylineLengthM = (points) => {
    let len = 0;
    for (let i = 1; i < (points || []).length; i++) len += haversineM(points[i - 1], points[i]);
    return len;
};

const validPt = (p) => p && Number.isFinite(p.lat) && Number.isFinite(p.lng) && !(p.lat === 0 && p.lng === 0);

/**
 * Leaflet positions for a segment. Segments are sliced leg by leg, so start → end is exact.
 * Without coordinates the station range is cut from the road polyline (scaled when the road was
 * redrawn after slicing, so the segment still lands in the right place).
 * @param {object} seg       { start, end, station_start_m, station_end_m }
 * @param {object} road      { polyline:[{lat,lng}], _lengthM?, _maxStation? }
 */
export function segmentPositions(seg, road) {
    if (validPt(seg?.start) && validPt(seg?.end)) return [[seg.start.lat, seg.start.lng], [seg.end.lat, seg.end.lng]];
    const poly = road?.polyline;
    const s0 = num(seg?.station_start_m); const s1 = num(seg?.station_end_m);
    if (!Array.isArray(poly) || poly.length < 2 || s0 === null || s1 === null || s1 <= s0) return [];
    const total = road._lengthM ?? polylineLengthM(poly);
    const maxStation = road._maxStation || total;
    const scale = maxStation > 0 && Math.abs(maxStation - total) / maxStation > 0.02 ? total / maxStation : 1;
    return slicePolyline(poly, s0 * scale, s1 * scale).map((p) => [p.lat, p.lng]);
}

export const midpointOf = (positions) => {
    if (!positions?.length) return null;
    if (positions.length === 2) return [(positions[0][0] + positions[1][0]) / 2, (positions[0][1] + positions[1][1]) / 2];
    return positions[Math.floor(positions.length / 2)];
};

// ── summaries ───────────────────────────────────────────────────────────────
export function bandCounts(segments) {
    const out = Object.fromEntries(BAND_KEYS.map((k) => [k, 0]));
    (segments || []).forEach((s) => { out[bandOf(s.pci).key] += 1; });
    return out;
}

export function summarize(segments) {
    const list = segments || [];
    const scored = list.filter((s) => num(s.pci) !== null);
    let w = 0; let p = 0;
    scored.forEach((s) => { const l = Math.max(1, num(s.length_m) || 1); w += l; p += l * num(s.pci); });
    const lengthKm = list.reduce((a, s) => a + Math.max(0, num(s.length_m) || 0), 0) / 1000;
    return {
        total: list.length,
        scored: scored.length,
        avgPci: w ? Math.round((p / w) * 10) / 10 : null,
        poor: scored.filter((s) => num(s.pci) < 50).length,
        critical: scored.filter((s) => num(s.pci) < 35).length,
        lengthKm: Math.round(lengthKm * 100) / 100,
        counts: bandCounts(list),
        // --- UPDATE (roads v1.4)
        ponding: list.filter((s) => s.ponding && s.ponding.band && s.ponding.band !== 'none').length,
        withGround: list.filter((s) => s.ground && (num(s.ground.start_m) !== null || num(s.ground.end_m) !== null)).length,
        // --- END UPDATE
    };
}

export const filterSegments = (segments, visibleBands) => {
    if (!visibleBands) return segments || [];
    const set = visibleBands instanceof Set ? visibleBands : new Set(visibleBands);
    return (segments || []).filter((s) => set.has(bandOf(s.pci).key));
};

/** Plain-language one-liner for tooltips and the AI helper. */
export function describeSegment(seg) {
    const band = bandOf(seg?.pci);
    const t = treatmentFor(seg?.pci);
    const bits = [
        seg?.pci !== null && seg?.pci !== undefined ? `PCI ${seg.pci} (${band.label.toLowerCase()})` : 'Not scored yet',
        num(seg?.potholes) ? `${seg.potholes} pothole(s)` : null,
        seg?.cracking && seg.cracking !== 'none' ? `${String(seg.cracking).replace(/_/g, ' ')} cracking` : null,
        num(seg?.rutting_mm) ? `${seg.rutting_mm} mm rutting` : null,
        t ? `→ ${t.label}` : null,
        // --- UPDATE (roads v1.4)
        seg?.ponding && seg.ponding.band && seg.ponding.band !== 'none'
            ? `💧 ${num(seg.ponding.depth_m) ? `${Math.round(num(seg.ponding.depth_m) * 100)} cm` : 'water'} forecast${num(seg.ponding.starts_hours) !== null ? ` from +${Math.round(num(seg.ponding.starts_hours))} h` : ''}`
            : null,
        seg?.ground && num(seg.ground.slope_pct) !== null ? `grade ${seg.ground.slope_pct}%` : null,
        // --- END UPDATE
    ];
    return bits.filter(Boolean).join(' · ');
}
